import { applyValidationError, resetErrors } from 'client-portal/utils/validate-record';
import { bannerError, callbackError } from 'client-portal/utils/error-handling';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';
import moment from 'moment-timezone';
import pht from 'client-portal/utils/persistent-hash-table';

export const ERROR_TOO_MANY_EMAIL_REQUESTS = 'tooManyEmailRequests';
export const ERROR_TOO_MANY_IP_REQUESTS = 'tooManyIpRequests';
export const ERROR_TOO_MANY_REQUESTS = 'tooManyRequests';
export const ERROR_INVALID = 'invalid';
export const ERROR_EXPIRED = 'expired';
export const ERROR_INVALID_EMAIL = 'invalidEmail';

const storedEmailKey = 'stored-email';
const storedEmailTtl = [14, 'days'];

export default class SignInService extends Service {
  @service intl;
  @service mixpanel;

  ERROR_TOO_MANY_EMAIL_REQUESTS = ERROR_TOO_MANY_EMAIL_REQUESTS;
  ERROR_TOO_MANY_IP_REQUESTS = ERROR_TOO_MANY_IP_REQUESTS;
  ERROR_TOO_MANY_REQUESTS = ERROR_TOO_MANY_REQUESTS;
  ERROR_INVALID_EMAIL = ERROR_INVALID_EMAIL;

  @tracked email = undefined;
  @tracked lastError = undefined;

  get storedEmail() {
    let data;
    try {
      data = JSON.parse(pht.get(storedEmailKey));
    } catch (err) {
      return null;
    }

    if (!data || typeof data !== 'object') {
      return null;
    }

    if (!data.email || !data.expiresAt || moment(data.expiresAt) < moment()) {
      return null;
    }

    return data.email;
  }

  set storedEmail(email) {
    pht.set(
      storedEmailKey,
      JSON.stringify({
        email,
        expiresAt: moment().add(...storedEmailTtl),
      })
    );
  }

  trackAttempt() {
    if ([ERROR_TOO_MANY_EMAIL_REQUESTS, ERROR_TOO_MANY_IP_REQUESTS].includes(this.lastError)) {
      this.mixpanel.track('visit: Client Portal too many requests page');
    } else if ([ERROR_EXPIRED, ERROR_INVALID].includes(this.lastError)) {
      this.mixpanel.track('visit: Client Portal Expired page');
    }
  }

  resetErrors() {
    this.lastError = undefined;
  }

  @task(function* (model) {
    this.resetErrors();
    this.email = model.email;
    resetErrors(model);
    yield model.validate();
    if (!model.validations.isValid) {
      applyValidationError(model);
      return;
    }

    try {
      yield model.save();
      return true;
    } catch (err) {
      callbackError(err, ({ status, title }) => {
        if (status === '429') {
          if (title === 'Email request limit reached') {
            this.lastError = ERROR_TOO_MANY_EMAIL_REQUESTS;
          } else if (title === 'IP request limit reached') {
            this.lastError = ERROR_TOO_MANY_IP_REQUESTS;
          } else {
            this.lastError = ERROR_TOO_MANY_REQUESTS;
          }
          this.trackAttempt();
        } else if (status === '422') {
          this.lastError = ERROR_INVALID_EMAIL;
          // Expiry shows in "check your inbox" page message.
          // Match valid email expiry time to prevent timing-based email existence detection
          model.set('expiresIn', '24 hours');
        } else if (!model.isValid) {
          this.lastError = ERROR_INVALID;
        } else {
          bannerError(err, { title: this.intl.t('messages.error.signInRequestFailed') });
        }

        return true;
      });
    }
  })
  requestTokenTask;

  @task(function* (model) {
    this.resetErrors();
    yield model.validate();
    if (!model.validations.isValid) {
      this.lastError = ERROR_INVALID;
      return;
    }

    try {
      yield model.save();
      switch (model.meta.status) {
        case 'verified':
          return true;
        case 'expired':
        case 'merged':
          this.lastError = model.meta.status;
          break;
        default:
          this.lastError = ERROR_INVALID;
      }
    } catch (err) {
      callbackError(err, ({ status }) => {
        if (status === '429') {
          this.lastError = ERROR_TOO_MANY_REQUESTS;
        } else {
          this.lastError = ERROR_INVALID;
        }
        return true;
      });
    }
  })
  verifyTokenTask;

  @task(function* (model) {
    this.resetErrors();
    yield model.validate();
    if (!model.validations.isValid) {
      return;
    }

    try {
      yield model.save();
      return true;
    } catch (err) {
      callbackError(err, ({ status }) => {
        if (status === '429') {
          this.lastError = ERROR_TOO_MANY_REQUESTS;
        } else {
          model.errors.add('pin', 'The code entered is incorrect or was already used');
        }
        return true;
      });
    }
  })
  verifyPinTask;
}
